import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet';
import { BookOpen, Bot, FileText, MessagesSquare, Palette, Search, Send, Settings } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../../Landing/footer';

const guides = [
  { group: 'Quick start', icon: Bot, title: 'Create your first chatbot', description: 'Create a chatbot and choose the business knowledge it should use.' },
  { group: 'Quick start', icon: Send, title: 'Publish on your website', description: 'Add the SmushChat widget to a website and verify the installation.' },
  { group: 'Knowledge', icon: FileText, title: 'Train from website pages', description: 'Select, crawl, and maintain approved website content.' },
  { group: 'Knowledge', icon: BookOpen, title: 'Upload files and documents', description: 'Add documents to a chatbot knowledge base and manage updates.' },
  { group: 'Customization', icon: Palette, title: 'Match your brand', description: 'Configure the chatbot identity, theme, and toggle button.' },
  { group: 'Customization', icon: Settings, title: 'Configure behaviour', description: 'Set instructions, fallback behaviour, forms, and proactive messages.' },
  { group: 'Conversations', icon: MessagesSquare, title: 'Review conversations', description: 'Use the inbox and analytics to review questions and improve answers.' },
];

const Page = styled.div`min-height:100vh;background:linear-gradient(180deg,#f0edff 0%,#fbfbfd 600px);color:#111827;`;
const Hero = styled.section`
  margin-top: -70px;
  padding: 140px 24px 54px;
  background: linear-gradient(180deg, #f0edff 0%, #fbfbfd 100%);
  text-align: center;
  @media (max-width: 968px) {
    margin-top: -72px;
  }
  h1{font-size:clamp(42px,6vw,68px);letter-spacing:-.05em;margin:12px 0;}
  p{max-width:680px;margin:0 auto;color:#606575;font-size:18px;line-height:1.7;}
`;
const Kicker = styled.div`color:#6d4aff;font-size:12px;font-weight:800;letter-spacing:.16em;`;
const SearchBox = styled.label`
  max-width:680px;margin:32px auto 0;background:#fff;border:1px solid #ddd8f4;border-radius:14px;padding:0 18px;display:flex;align-items:center;gap:12px;box-shadow:0 14px 35px rgba(48,36,88,.08);
  input{width:100%;border:0;outline:0;background:transparent;padding:17px 0;font-size:16px;color:#151821;}
  svg{color:#77728b;}
`;
const DocsShell = styled.div`max-width:1180px;margin:0 auto;padding:56px 24px 100px;display:grid;grid-template-columns:230px 1fr;gap:54px;@media(max-width:800px){grid-template-columns:1fr;gap:24px;}`;
const Sidebar = styled.aside`position:sticky;top:100px;align-self:start;border-right:1px solid #e7e5ee;padding-right:24px;@media(max-width:800px){position:static;border-right:0;border-bottom:1px solid #e7e5ee;padding:0 0 20px;}`;
const SidebarTitle = styled.div`font-size:12px;font-weight:800;letter-spacing:.12em;color:#878394;margin-bottom:14px;`;
const SidebarItem = styled.a`display:block;text-decoration:none;color:#4f5361;padding:8px 10px;border-radius:8px;font-size:14px;&:hover{background:#f0edff;color:#47338f;}`;
const Content = styled.div`min-width:0;`;
const Group = styled.section`scroll-margin-top:100px;margin-bottom:54px;h2{font-size:28px;letter-spacing:-.03em;margin:0 0 18px;}`;
const Cards = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:14px;@media(max-width:650px){grid-template-columns:1fr;}`;
const GuideCard = styled.article`
  border:1px solid #e3e0ee;background:#fff;border-radius:14px;padding:22px;min-height:160px;
  .top{display:flex;justify-content:space-between;align-items:start;color:#6d4aff}
  h3{font-size:18px;margin:25px 0 8px;}p{margin:0;color:#666b78;line-height:1.6;font-size:14px;}
`;
const Empty = styled.div`padding:50px;border:1px dashed #d8d4e5;border-radius:14px;text-align:center;color:#686474;`;

export default function GuidesPage() {
  const [query, setQuery] = useState('');
  const groups = ['Quick start', 'Knowledge', 'Customization', 'Conversations'];
  const filtered = useMemo(() => guides.filter((guide) => `${guide.title} ${guide.description} ${guide.group}`.toLowerCase().includes(query.toLowerCase())), [query]);

  return (
    <Page>
      <Helmet>
        <title>SmushChat Guides and Product Documentation | SmushLabs</title>
        <meta name="description" content="SmushChat user guides for setup, knowledge sources, customization, publishing, conversations, and analytics." />
        <link rel="canonical" href="https://smushlabs.ai/guides" />
      </Helmet>
      <Navigation />
      <main>
        <Hero>
          <Kicker>SMUSHCHAT GUIDES</Kicker>
          <h1>How can we help?</h1>
          <p>A documentation home for setting up, training, customizing, publishing, and improving SmushChat. Detailed how-to articles will be added here.</p>
          <SearchBox><Search size={20}/><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search guides" aria-label="Search guides" /></SearchBox>
        </Hero>
        <DocsShell>
          <Sidebar><SidebarTitle>USER GUIDES</SidebarTitle>{groups.map((group) => <SidebarItem key={group} href={`#${group.toLowerCase().replace(' ', '-')}`}>{group}</SidebarItem>)}</Sidebar>
          <Content>
            {filtered.length === 0 ? <Empty>No guide topics match “{query}”.</Empty> : groups.map((group) => {
              const groupGuides = filtered.filter((guide) => guide.group === group);
              if (!groupGuides.length) return null;
              return <Group id={group.toLowerCase().replace(' ', '-')} key={group}><h2>{group}</h2><Cards>{groupGuides.map(({ icon: Icon, title, description }) => <GuideCard key={title}><div className="top"><Icon size={22}/></div><h3>{title}</h3><p>{description}</p></GuideCard>)}</Cards></Group>;
            })}
          </Content>
        </DocsShell>
      </main>
      <Footer />
    </Page>
  );
}
